import { PlayerController } from './player.controller';
import { computed, signal } from '@angular/core';
import { MediaToPlay, PlayerItemToPlay, PlayerSegment } from '../models';
import { ScrubBarSegment } from './scrub-bar-segment.interface';
import { PSNotification, PSNotificationTypes } from '@hidat/huijs-player';
import { nString } from '@hidat/huijs-common';

/**
 * Extends the player controller with signals for the current SchedulingItem, associated media items,
 * and optionally, scrub bar segments.
 */
export class SiPlayerController extends PlayerController {
  protected npMediaOffset = 0;
  protected mediaItems: MediaToPlay[] | undefined;
  protected hasScrubBarSegments = true;

  protected npSchedulingItem = signal<PlayerItemToPlay | undefined>(undefined);
  protected npSchedulingItemDuration = signal<number>(0);
  protected npMediaItems = signal<MediaToPlay[] | undefined>(undefined);
  protected npMediaItemIndex = signal<number>(0);

  protected readonly ranOutOfContent = signal(false);

  protected override setInitialState() {
    this.resetPlaybackPositionOnFinished = false; // We want the scrub bar to show the whole segment played
    super.setInitialState();
  }

  protected override onMediaLoaded() {
    const newMedia = this.mediaPlaying;
    const newItem = newMedia?.schedulingItem;
    if (newItem) {
      this.ranOutOfContent.set(false);
      const currentItem = this.npSchedulingItem();
      if (
        currentItem === undefined ||
        (currentItem.uid && currentItem.uid !== newItem.uid)
      ) {
        // We have a new scheduling item, rebuild it all

        this.npSchedulingItem.set(newItem);
        this.mediaItems = newItem.getMediaToPlay();
        this.npMediaOffset = 0;
        let startPosition = 0;
        let currentMtpIndex = 0;
        let index = 0;
        for (const mtp of this.mediaItems) {
          if (newMedia.uid === mtp.uid) {
            currentMtpIndex = index;
            this.npMediaOffset = startPosition;
          }
          startPosition += mtp.duration;
          index++;
        }
        this.npMediaItems.set(this.mediaItems);
        this.npSchedulingItemDuration.set(startPosition);
        this.npMediaItemIndex.set(currentMtpIndex);
        this.onSchedulingItemChanged(newItem, currentItem);
      } else {
        if (this.mediaItems) {
          let currentMtpIndex = 0;
          let index = 0;
          let startPosition = 0;
          for (const mtp of this.mediaItems) {
            if (newMedia.uid === mtp.uid) {
              currentMtpIndex = index;
              this.npMediaOffset = startPosition;
            }
            startPosition += mtp.duration;
            index++;
          }
          this.npMediaItemIndex.set(currentMtpIndex);
        }
      }

      //console.debug('ITP:', newItem.uid, ' Duration:', this.npSchedulingItemDuration(), ' Index:', this.npMediaItemIndex())
    }
  }

  /**
   * Current position of playback in the whole current scheduling item (i.e., show)
   */
  public npSchedulingItemPosition = computed(() => {
    const p = this.positionSeconds() ?? 0;
    const offset = this.npMediaOffset ?? 0;
    return offset + p;
  });

    protected positionSpoken = computed(() => {
    const p = this.positionSeconds();
    const pm = this.positionMinutes();
    const dm = this.durationMinutes();
    let speech: string | undefined;
    if (dm > 0) {
      if (p === 0) {
        speech = `At the start of ${dm} minutes`;
      } else if (p < 60) {
        speech = `At ${p} seconds of ${dm} minutes`;
      } else {
        speech = `At minute ${pm} of ${dm} minutes`;
      }
    } else {
      speech = 'No Audio'
    }
    return speech;
  })

  /**
   * Returns the current scrub bar segments.
   * Tried to make this more efficient by persisting the segments and updating them, but in the end functional programming won...
   * @protected
   */
  protected npScrubBarSegments = computed<ScrubBarSegment[]>(() => {
    const segments: ScrubBarSegment[] = [];
    if (this.hasScrubBarSegments) {
      const siDuration = this.npSchedulingItemDuration();
      const miPosition = this.position();
      const playingIndex = this.npMediaItemIndex();

      if (this.mediaItems) {
        let segmentStart = 0;
        let index = 0;
        for (const media of this.mediaItems) {
          const percOfTotal = (media.duration / siDuration) * 100.0;
          let played = false;
          let playing = false;
          let position = 0;
          let percPlayed = 0;
          if (index < playingIndex) {
            played = true;
            position = media.duration;
            percPlayed = 100;
          } else if (index === playingIndex) {
            playing = true;
            position = miPosition;
            percPlayed = (position / media.duration) * 100;
          }
          let name: string | undefined;
          if (media._segment) {
            name = this.scrubBarSegmentName(media._segment);
          }

          const segment: ScrubBarSegment = {
            index,
            played,
            playing,
            percOfTotal,
            duration: media.duration,
            position,
            name,
            percPlayed,
          };
          index++;
          segmentStart += media.duration;
          segments.push(segment);
        }
      }
    }
    return segments;
  });

  protected onSchedulingItemChanged(
    newItem: PlayerItemToPlay,
    oldItem: PlayerItemToPlay | undefined
  ) {
    //console.debug('Loading ItemToPlay: ', newItem.uid);
  }

  protected override handlePlaybackNotification(notification: PSNotification) {
    switch (notification.type) {
      case PSNotificationTypes.OUT_OF_CONTENT:
      case PSNotificationTypes.MIX_PLAYED_OUT:
        this.ranOutOfContent.set(true);
        this.onOutOfContent();
        break;
    }
  }

  protected onOutOfContent() {
  }

  protected scrubBarSegmentName(segment: PlayerSegment): string | undefined {
    if (segment.hourInShow) {
      return 'Hour ' + segment.hourInShow.toString();
    }
    return segment.name;
  }

  protected gotoSegment(mediaItemIndex: number, eventSource?: nString) {
    const medias = this.npMediaItems();
    if (medias && mediaItemIndex < medias.length) {
      const mtp = medias[mediaItemIndex];
      this.playbackService.playQueuedMediaItem(mtp);
      if (eventSource !== null) {
        this.eventTracker.playSegment(eventSource ?? this.eventSource);
      }
    }
  }

  protected skipToNextSegment = () => {
    this.playbackService.skipSegment();
    this.eventTracker.playSkipNextSegment(this.eventSource);
  };

  protected skipToPreviousSegment = () => {
    if (this.playing() && this.mediaPlaying) {
      const position = this.position();
      if (this.mediaPlaying.firstInItem) {
        if (position > 0) {
          this.playerEngine.setPosition(0);
          this.eventTracker.playRestartSegment(this.eventSource);
        }
      } else {
        if (position > 60) {
          this.playerEngine.setPosition(0);
          this.eventTracker.playRestartSegment(this.eventSource);
        } else {
          const ci = this.npMediaItemIndex();
          if (ci > 0) {
            this.gotoSegment(ci - 1, null);
            this.eventTracker.playSkipPreviousSegment(this.eventSource);
          }
        }
      }
    }
  };

  public override fastForward() {
    console.debug('move forward');
    if (this.playing() && this.mediaPlaying) {
      const skipSeconds = this.skipSeconds(false);
      if (this.duration() - this.position() > skipSeconds) {
        this.playerEngine.setPosition((this.position() + skipSeconds) * 1000);
      } else {
        if (!this.mediaPlaying.lastInItem) {
          this.playbackService.skipSegment();
        }
      }
    }
  }
}
